package com.tixforgigs.common.core.util

import com.tixforgigs.common.CommonBuildKonfig
import com.tixforgigs.common.core.extensions.isNull
import com.tixforgigs.common.core.extensions.tryCatchSentry
import com.tixforgigs.common.core.models.ui.InputState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mUnresolvedOrderMetadataState
import com.tixforgigs.tarami.GlobalMessage.textMonthApril
import com.tixforgigs.tarami.GlobalMessage.textMonthAugust
import com.tixforgigs.tarami.GlobalMessage.textMonthDecember
import com.tixforgigs.tarami.GlobalMessage.textMonthFebruary
import com.tixforgigs.tarami.GlobalMessage.textMonthJanuary
import com.tixforgigs.tarami.GlobalMessage.textMonthJuly
import com.tixforgigs.tarami.GlobalMessage.textMonthJune
import com.tixforgigs.tarami.GlobalMessage.textMonthMarch
import com.tixforgigs.tarami.GlobalMessage.textMonthMay
import com.tixforgigs.tarami.GlobalMessage.textMonthNovember
import com.tixforgigs.tarami.GlobalMessage.textMonthOctober
import com.tixforgigs.tarami.GlobalMessage.textMonthSeptember
import io.ktor.http.*
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.*
import kotlinx.datetime.*
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant


@OptIn(ExperimentalTime::class)
object DateTimeUtil {

  val currentInstant get() = Clock.System.now()
  val currentTimestamp get() = Clock.System.now().toEpochMilliseconds()

  enum class Type {
    DATE, TIME, DATETIME
  }

  fun Instant.toDateTimeString(
    showDay: Boolean = true,
    showMonth: Boolean = true,
    showYear: Boolean = true,
    showHours: Boolean = true,
    showMinutes: Boolean = true,
    showSeconds: Boolean = true,
  ) = toLocalDateTime(TimeZone.currentSystemDefault())
    .toDateTimeString(
      showDay = showDay,
      showMonth = showMonth,
      showYear = showYear,
      showHours = showHours,
      showMinutes = showMinutes,
      showSeconds = showSeconds
    )

  fun Instant.toDateString(): String = toDateTimeString(
    showHours = false,
    showMinutes = false,
    showSeconds = false
  )

  fun Instant.roundOffToMinute(): Instant {
    val flooredSeconds = epochSeconds.floorDiv(60L).times(60L)
    return Instant.fromEpochSeconds(flooredSeconds)
  }

  fun Long.toDateTimeString(
    showDay: Boolean = true,
    showMonth: Boolean = true,
    showYear: Boolean = true,
    showHours: Boolean = true,
    showMinutes: Boolean = true,
    showSeconds: Boolean = true,
  ) = Instant.fromEpochMilliseconds(this)
    .toDateTimeString(
      showDay = showDay,
      showMonth = showMonth,
      showYear = showYear,
      showHours = showHours,
      showMinutes = showMinutes,
      showSeconds = showSeconds
    )

  fun String.toISO8601(): String? {
    val regex = Regex("\\d{4}-\\d{2}-\\d{2}$")
    return if (regex.matches(this)) {
      LocalDate.parse(this)
        .atTime(0, 0)
        .toInstant(TimeZone.currentSystemDefault())
        .toString()
    } else null
  }

  @Deprecated("Not needed anymore")
  fun String.parseRfc1123ToInstant(): Instant {
    return Instant.fromEpochMilliseconds(fromHttpToGmtDate().timestamp)
  }

  val currentDate = currentInstant.toLocalDateTime(TimeZone.currentSystemDefault()).date
  val mOrderBirthdateInput = MutableStateFlow<InputState<LocalDate?>>(
    InputState(runCatching { LocalDate.parse(CommonBuildKonfig.TEST_DOB) }.getOrNull(), CommonBuildKonfig.TEST)
  )
  val birthdateInputEnabled = mUnresolvedOrderMetadataState.map { order -> order.isNull }
    .stateIn(MainScope(), SharingStarted.Eagerly, mUnresolvedOrderMetadataState.value.isNull)
  val birthdateYears = MutableStateFlow((currentDate.year downTo currentDate.year - 100).toList())
  val birthdateMonths = MutableStateFlow(Month.entries.toList())
  val mBirthdateDayInput = MutableStateFlow(
    InputState(mOrderBirthdateInput.value.inputValue?.day, mOrderBirthdateInput.value.isValid)
  )
  val mBirthdateMonthInput = MutableStateFlow(
    InputState(mOrderBirthdateInput.value.inputValue?.month, mOrderBirthdateInput.value.isValid)
  )
  val mBirthdateYearInput = MutableStateFlow(
    InputState(mOrderBirthdateInput.value.inputValue?.year, mOrderBirthdateInput.value.isValid)
  )
  val birthdateDays = combine(mBirthdateMonthInput, mBirthdateYearInput) {
    monthInput, yearInput ->
    val selectedMonth = monthInput.inputValue
    val selectedYear = yearInput.inputValue
    if (selectedMonth != null && selectedYear != null) {
      (1..daysInMonth(selectedYear, selectedMonth)).toList()
    } else {
      (1..31).toList()
    }
  }.stateIn(MainScope(), SharingStarted.Eagerly, (1..31).toList())

  fun syncBirthdateInputs(date: LocalDate?) {
    mBirthdateDayInput.update { it.copy(inputValue = date?.day, isValid = date != null) }
    mBirthdateMonthInput.update { it.copy(inputValue = date?.month, isValid = date != null) }
    mBirthdateYearInput.update { it.copy(inputValue = date?.year, isValid = date != null) }
  }

  init {
    syncBirthdateInputs(mOrderBirthdateInput.value.inputValue)

    birthdateDays.onEach { validDays ->
      if (mBirthdateDayInput.value.inputValue !in validDays) {
        mBirthdateDayInput.update { it.copy(inputValue = null, isValid = false) }
      }
    }.launchIn(MainScope())

    combine(mBirthdateDayInput, mBirthdateMonthInput, mBirthdateYearInput) { dayInput, monthInput, yearInput ->
      Triple(dayInput, monthInput, yearInput)
    }.onEach { (dayInput, monthInput, yearInput) ->
      val birthdate = dayInput.inputValue?.let { day ->
        monthInput.inputValue?.let { month ->
          yearInput.inputValue?.let { year ->
            runCatching { LocalDate(year, month.ordinal + 1, day) }.getOrNull()
          }
        }
      }

      val isValid = birthdate != null && birthdate <= currentDate
      mOrderBirthdateInput.update { current ->
        if (current.inputValue == birthdate && current.isValid == isValid) current
        else current.copy(inputValue = birthdate, isValid = isValid)
      }
    }.launchIn(MainScope())
  }

  fun Month.toLocalizedLabel(): String = when (this) {
    Month.JANUARY -> textMonthJanuary()
    Month.FEBRUARY -> textMonthFebruary()
    Month.MARCH -> textMonthMarch()
    Month.APRIL -> textMonthApril()
    Month.MAY -> textMonthMay()
    Month.JUNE -> textMonthJune()
    Month.JULY -> textMonthJuly()
    Month.AUGUST -> textMonthAugust()
    Month.SEPTEMBER -> textMonthSeptember()
    Month.OCTOBER -> textMonthOctober()
    Month.NOVEMBER -> textMonthNovember()
    Month.DECEMBER -> textMonthDecember()
  }

  fun Instant.toInputValue(
    type: Type = Type.DATE,
    timeZone: TimeZone = TimeZone.currentSystemDefault()
  ) = when (type) {
    Type.DATE -> toLocalDateTime(timeZone).date.toString()
    Type.TIME -> toLocalDateTime(timeZone).time.toString()
    Type.DATETIME -> toLocalDateTime(timeZone).toString()
  }

  fun String.toInstant(): Instant? {
    val regex = Regex("\\d{4}-\\d{2}-\\d{2}$")
    return tryCatchSentry {
      if (regex.matches(this)) {
        LocalDate.parse(this)
          .atTime(0, 0)
          .toInstant(TimeZone.currentSystemDefault())
      } else {
        null
      }
    }.getOrNull()
  }

  fun LocalTime.toTimeString(): String {
    val hours = hour.twoDigits()
    val minutes = minute.twoDigits()
    val seconds = second.twoDigits()
    return if (hour > 0) "$hours:$minutes:$seconds" else "$minutes:$seconds"
  }


  private fun LocalDateTime.toDateTimeString(
    showDay: Boolean = true,
    showMonth: Boolean = true,
    showYear: Boolean = true,
  ): String {
    val day = day.twoDigits()
    val month = month.number.twoDigits()
    val year = year.toString()
    return "".plus(if (showDay) day else "")
      .plus(if (showMonth) "${if (showDay) "." else ""}$month" else "")
      .plus(if (showYear) "${if (showMonth) "." else ""}$year" else "")
  }

  private fun LocalDateTime.toDateTimeString(
    showDay: Boolean = true,
    showMonth: Boolean = true,
    showYear: Boolean = true,
    showHours: Boolean = true,
    showMinutes: Boolean = true,
    showSeconds: Boolean = true,
  ): String {
    val day = day.twoDigits()
    val month = month.number.twoDigits()
    val year = year.toString()
    val hours = hour.twoDigits()
    val minutes = minute.twoDigits()
    val seconds = second.twoDigits()
    return "".plus(if (showDay) day else "")
      .plus(if (showMonth) "${if (showDay) "." else ""}$month" else "")
      .plus(if (showYear) "${if (showMonth) "." else ""}$year" else "")
      .plus(if (showHours) " $hours" else "")
      .plus(if (showMinutes) ":$minutes" else "")
      .plus(if (showSeconds) ":$seconds" else "")
      .plus(if (showHours && showMinutes) " Uhr" else "")
  }

  private fun Int.twoDigits(): String {
    return this.toString().padStart(2, '0')
  }

  private fun daysInMonth(year: Int, month: Month): Int = (31 downTo 28)
    .first { day -> runCatching { LocalDate(year, month.ordinal + 1, day) }.isSuccess }
}
